import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import PropTypes from 'prop-types';
import ArgonTypography from 'components/ArgonTypography';
import ProductDetailsReview from '../reviewComponents/ProductDetailsReview';
import ComponentsReview from '../reviewComponents/ComponentsReview';
import RevenueAttributes from '../reviewComponents/RevenueAttributes';
import ProductRulesReview from '../reviewComponents/ProductRulesReview';
import FulfillmentReview from '../reviewComponents/FulfillmentReview';
import DecompositionReview from '../reviewComponents/DecompositionReview';
import PricingModelReview from '../reviewComponents/PricingModelReview';
import PricebooksReview from '../reviewComponents/PricebooksReview';
import CPQAttributesReview from '../reviewComponents/CPQAttributesReview';
import ConsumptionScheduleReview from '../reviewComponents/ConsumptionScheduleReview';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { styled } from '@mui/material/styles';
import { useNotifications } from 'context/NotificationContext';
import { useProductReviewData } from 'hooks/useProductReviewData'; // Adjust path if needed

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  '&.MuiToggleButton-root': {
    border: 'none',
    background: 'transparent',
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.selected,
      '&:hover': { backgroundColor: theme.palette.action.selected },
    },
  },
}));

function StageReview({
  org,
  isRevenue,
  productFields,
  selectedProducts,
  onModifiedProducts,
  setCommitProducts,
  databaseMajorVersions,
  userId, // Pass userId and orgId to the hook
  orgId,
}) {
  const [showSameFields, setShowSameFields] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const { addNotification } = useNotifications();

  // --- Use the custom hook to handle all data logic ---
  const { processedData, loading, error, comparisonErrors } = useProductReviewData({
    userId,
    orgId,
    selectedProducts,
    productFields,
    isRevenue,
    databaseMajorVersions,
  });

  // --- Effect to handle notifications and parent state updates ---
  useEffect(() => {
    // Update parent components when data processing is complete
    onModifiedProducts(processedData);
    setCommitProducts(processedData.map((diff) => diff.product).filter(Boolean));

    // Handle notifications for comparison errors
    if (comparisonErrors.length > 0) {
      const errorDetails = comparisonErrors.map((e) => e.message).join('; ');
      setSnackbarMessage(`Some products could not be compared. See notifications for details.`);
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      addNotification({
        title: ['Warning', 'Comparison Failed'],
        severity: 'warning',
        details: errorDetails,
      });
    }
  }, [processedData, comparisonErrors, onModifiedProducts, setCommitProducts, addNotification]);

  // --- Effect to handle major fetch errors ---
  useEffect(() => {
    if (error) {
      setSnackbarMessage(error);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      addNotification({ title: ['Error', 'Failed to Load Review Data'], severity: 'error', details: error });
    }
  }, [error, addNotification]);

  const tabData = useMemo(
    () => [
      { label: 'Product Details', key: 'productDetails' },
      { label: 'Components', key: 'components' },
      ...(isRevenue
        ? [
            { label: 'Attributes', key: 'revenueAttributes' },
            { label: 'Fulfillment', key: 'fulfillment' }, // <-- NEW
            { label: 'Decomposition', key: 'decomposition' }, // <-- NEW
          ]
        : [
            { label: 'Product Rules', key: 'productRules' }, // <-- CPQ only
            { label: 'Rate Cards', key: 'rateCards' }, // <-- CPQ Consumption Schedules
            { label: 'Attributes', key: 'cpqAttributes' }, // <-- CPQ Attributes
          ]),
      { label: 'Pricing Model', key: 'pricingModel' },
      { label: 'Pricebooks', key: 'pricebooks' },
    ],
    [isRevenue]
  );

  const [activeReviewStage, setActiveReviewStage] = useState('productDetails');
  const handleTabChange = (event, newValue) => setActiveReviewStage(newValue);
  const handleShowSameChange = (event, newValue) => {
    if (newValue !== null) setShowSameFields(newValue);
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason !== 'clickaway') setSnackbarOpen(false);
  };

  const renderReviewContent = () => {
    const commonProps = { selectedOrg: org, productData: processedData, showSameFields };
    switch (activeReviewStage) {
      case 'productDetails':
        return <ProductDetailsReview {...commonProps} />;
      case 'components':
        return <ComponentsReview {...commonProps} />;
      case 'revenueAttributes':
        return <RevenueAttributes {...commonProps} />;
      case 'productRules':
        return <ProductRulesReview {...commonProps} />;
      case 'rateCards':
        return <ConsumptionScheduleReview {...commonProps} />;
      case 'cpqAttributes':
        return <CPQAttributesReview {...commonProps} />;
      case 'fulfillment':
        return <FulfillmentReview {...commonProps} />;
      case 'decomposition':
        return <DecompositionReview {...commonProps} />;
      case 'pricingModel':
        return <PricingModelReview {...commonProps} />;
      case 'pricebooks':
        return <PricebooksReview {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <ArgonBox py={3}>
      <Card>
        <ArgonBox p={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Tabs
              value={activeReviewStage}
              onChange={handleTabChange}
              aria-label="review-tabs"
              indicatorColor="primary"
              textColor="primary"
              allowScrollButtonsMobile
              sx={{ flexGrow: 1, marginRight: '1rem' }}
            >
              {tabData.map((tab) => (
                <Tab key={tab.key} label={tab.label} value={tab.key} />
              ))}
            </Tabs>
            <ToggleButtonGroup
              value={showSameFields}
              exclusive
              onChange={handleShowSameChange}
              aria-label="Show Same Fields"
              size="small"
            >
              <StyledToggleButton value={false} aria-label="Hide Same Fields">
                <VisibilityOffIcon />
              </StyledToggleButton>
              <StyledToggleButton value={true} aria-label="Show Same Fields">
                <VisibilityIcon />
              </StyledToggleButton>
            </ToggleButtonGroup>
          </Box>
          <ArgonBox mt={3}>
            <Card>
              {loading ? (
                <ArgonBox p={3} display="flex" alignItems="center" justifyContent="center" minHeight="200px">
                  <CircularProgress color="info" />
                </ArgonBox>
              ) : error && processedData.length === 0 ? (
                <ArgonBox p={3} display="flex" alignItems="center" justifyContent="center" minHeight="200px">
                  <ArgonTypography variant="body1" color="error">
                    {error}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                renderReviewContent()
              )}
            </Card>
          </ArgonBox>
        </ArgonBox>
      </Card>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} variant="filled" severity={snackbarSeverity} sx={{ alignItems: 'center' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ArgonBox>
  );
}

StageReview.propTypes = {
  selectedProducts: PropTypes.array,
  userId: PropTypes.string.isRequired,
  orgId: PropTypes.string.isRequired,
  org: PropTypes.object.isRequired,
  porductFields: PropTypes.array.isRequired,
  onModifiedProducts: PropTypes.func.isRequired,
  setCommitProducts: PropTypes.func.isRequired,
  isRevenue: PropTypes.bool,
  databaseMajorVersions: PropTypes.array,
};

export default StageReview;
